<template>
  <div ref="mainElRef" class="chat-thread-messages-message-list">
    <div class="px-3">
      <Message
        v-for="message in messages"
        :key="message.id"
        :message="message"
        class="mb-2"
        :is-latest-viewed="isLatestViewed(message)"
        @onViewStart="onViewStart"
      />
      <NewMessageForm
        ref="newMessageFormRef"
        :thread-id="threadId"
        @on-message-send-start="onMessageSendStart"
        @on-message-send-finish="onMessageSendFinish"
        @change="scrollToBottom()"
      />
      <DisplayNameInfo />
    </div>
  </div>
</template>

<script>
import useMessages from './useMessages'
import Message from './Message'
import NewMessageForm from './NewMessageForm'
import DisplayNameInfo from 'pages/chat/DisplayNameInfo'
import { debounce, orderBy } from 'lodash'

import { ref, computed, onBeforeUnmount, watch } from '@vue/composition-api'

const POLL_MESSAGES_INTERVAL = 60 * 1000 // in ms

export default {
  name: 'Messages',
  components: { DisplayNameInfo, Message, NewMessageForm },
  props: {
    threadId: {
      type: String,
      default: '',
    },
  },
  setup(props, { root }) {
    const mainElRef = ref(null)
    const newMessageFormRef = ref(null)
    const user = computed(() => root.$store.state.user.data)
    const {
      messages: allMessages,
      isMessagesFetchInProgress,
      fetchMessages,
      markMessageAsRead,
    } = useMessages({})
    const messages = computed(() => {
      return orderBy(allMessages.value, 'ts')
    })

    const scrollToBottom = () => {
      const $el = mainElRef.value
      root.$nextTick(() => {
        $el.scrollTo(0, $el.scrollHeight + 50)
      })
    }

    const fetch = async () => {
      return await fetchMessages({ threadId: props.threadId })
    }

    watch(
      () => [props.threadId],
      () => {
        ;(async () => {
          await fetch()
          scrollToBottom()
          if (!user.value.display_name) return
          newMessageFormRef.value.focus()
        })()
      },
    )

    const pollMessagesIntervalId = setInterval(fetch, POLL_MESSAGES_INTERVAL)

    onBeforeUnmount(() => {
      clearInterval(pollMessagesIntervalId)
    })

    const onMessageSendStart = (message) => {
      allMessages.value.push({
        ...message,
        author: {
          id: user.value.id,
          display_name: user.value.display_name,
        },
      })
    }

    const onMessageSendFinish = (message) => {
      allMessages.value.splice(allMessages.value.length - 1, 1, message)
    }

    const markMessageAsReadDebounced = debounce(async (args) => {
      await markMessageAsRead(args)
      root.$store.dispatch('chat/fetchThreads')
    }, 300)

    const latestViewedMessage = ref(null)
    const latestViewedMessageTs = ref(null) // when user started looking at this message
    const onViewStart = (message) => {
      if (
        !latestViewedMessage.value ||
        (latestViewedMessage.value && message.ts > latestViewedMessage.value.ts)
      ) {
        latestViewedMessage.value = message
        latestViewedMessageTs.value = new Date()
      }
      if (!message.already_read) {
        markMessageAsReadDebounced({ threadId: props.threadId, id: message.id })
      }
    }
    const isLatestViewed = (message) => {
      return message == latestViewedMessage.value
    }

    return {
      messages,
      isMessagesFetchInProgress,
      onMessageSendStart,
      onMessageSendFinish,
      mainElRef,
      scrollToBottom,

      latestViewedMessage,
      isLatestViewed,
      onViewStart,

      newMessageFormRef,
    }
  },
}
</script>

<style lang="scss" scoped>
.chat-thread-messages-message-list {
  height: calc(100vh - 65px - 3 * 16px);
  overflow: auto;
}
</style>
